import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { Spin } from '@robinpowered/ui-kit';
import { useGetHomepage } from 'hooks/useGetHomepage';
import { useHomepagePath } from 'hooks/useHomepagePath';
import { useAuthContext, useApolloContext } from 'contexts';
import { PageMessage } from './PageMessage';
import { useTranslation } from 'react-i18next';
import { useGetOrganizationsForSwitcherQuery } from 'generated/graphql';

export const HomepageRedirect = (): JSX.Element => {
  const { t } = useTranslation('common');
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const navigate = useNavigate();
  const { tenantId } = useApolloContext();
  const {
    currentOrg,
    loading: authLoading,
    setOrgSlug,
    orgSlug: contextOrgSlug,
    error: authError,
  } = useAuthContext();
  const { homepage, loading: homepageLoading } = useGetHomepage({
    skip: !tenantId,
  });
  const homepagePath = useHomepagePath(homepage);

  // When no orgSlug in the URL, fetch the first org to redirect to.
  const { data: switcherData, loading: switcherLoading } =
    useGetOrganizationsForSwitcherQuery({
      variables: { first: 1 },
      skip: !!orgSlug,
    });

  const firstOrgSlug =
    switcherData?.getCurrentUserAndOrgs?.organizationsForSwitcher
      ?.organizations[0]?.slug;

  const targetOrgSlug = useMemo(() => {
    if (orgSlug) return orgSlug;
    return firstOrgSlug ?? null;
  }, [orgSlug, firstOrgSlug]);

  // After auth loads, organizationBySlug confirms validity:
  // null means the slug doesn't match any org the user belongs to.
  const isValidOrgSlug = useMemo(() => {
    if (!orgSlug) return true;
    if (authLoading) return null;
    return !!currentOrg;
  }, [orgSlug, authLoading, currentOrg]);

  const loading =
    authLoading ||
    switcherLoading ||
    (targetOrgSlug && !tenantId) ||
    (tenantId && homepageLoading);

  // Set orgSlug in AuthContext from URL params or auto-select first org.
  useEffect(() => {
    if (
      targetOrgSlug &&
      contextOrgSlug !== targetOrgSlug &&
      isValidOrgSlug !== false
    ) {
      setOrgSlug(targetOrgSlug);
    }
  }, [targetOrgSlug, contextOrgSlug, setOrgSlug, isValidOrgSlug]);

  useEffect(() => {
    if (!loading && homepagePath && targetOrgSlug && isValidOrgSlug) {
      navigate(`/${targetOrgSlug}/${homepagePath}`, { replace: true });
    }
  }, [homepagePath, loading, targetOrgSlug, navigate, isValidOrgSlug]);

  // Show error if user is not authenticated.
  // If the query finished and returned no data / an error, the user likely
  // has no access token or no orgs.
  if (
    !authLoading &&
    !switcherLoading &&
    !currentOrg &&
    !firstOrgSlug &&
    (authError || !orgSlug)
  ) {
    return (
      <PageMessage
        title={t('errors.auth_error.title')}
        message={t('errors.auth_error.message')}
      />
    );
  }

  // Show error if orgSlug is invalid (only after auth query has resolved).
  if (!authLoading && orgSlug && isValidOrgSlug === false) {
    return <PageMessage title={t('errors.invalid_org_slug', { orgSlug })} />;
  }

  // Show loading spinner while loading
  return (
    <CenteredContainer>{loading && <Spin size="large" />}</CenteredContainer>
  );
};

const CenteredContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;
